import {
  DocketDisplayItem,
  DocketDisplayItemType,
  DocketDisplayOrder,
  DocketDisplayOrderItem,
  DocketDisplayOtherItem,
  DocketDetailsProps,
  DocketDisplayContinueItem,
  DocketDisplayContinuedItem,
} from '../../common/types';
import React from 'react';
import {
  OrderType,
  DocketStatus,
  SalesChannel,
  DocketItemStatus,
  Docket,
  NotifierDocketItems,
} from '@oolio-group/domain';
import {
  getOrderTypeName,
  getDockerHeaderTitle,
  getHeaderColorByOrderType,
  getPartnerSalesChannelName,
} from '../../utils/docketHelper/transformDockets';
import DocketItemHeader from './DocketItemEntities/DocketItemHeader';
import DocketDisplayItemComponent from './DocketItemEntities/DocketDisplayItem';
import DocketItemBase from './DocketItemEntities/DocketItemBase';
import DocketDueTime from './DocketItemEntities/DocketDueTime';
import DocketItemContinue from './DocketItemEntities/DocketItemContinue';
import DocketItemReCall from './DocketItemEntities/DocketRecallButton';
import DocketVoidLabel from './DocketItemEntities/DocketVoidLabel';
import DocketTransferredLabel from './DocketItemEntities/DocketTransferredLabel';
import DocketRecalledLabel from './DocketItemEntities/DocketRecalledLabel';
import DocketItemNotes from './DocketItemEntities/DocketItemNotes';
import TrainingDocketHeader from './DocketItemEntities/TrainingDocketHeader';
import DocketOtherItemsLabel from './DocketItemEntities/DocketOtherItemsLabel';
import DocketTitleBar from './DocketItemEntities/DocketTitleBar';
import { selectedDocketVar, bulkSelectVar } from '../../state/apolloVars';

export interface DocketItemProps {
  item?: DocketDisplayItem;
  status: DocketStatus;
  onPressDocketHeader?: (
    docketId: string,
    notifierItems: NotifierDocketItems[] | undefined,
  ) => Promise<Docket>;
  onPressDocketItem?: (
    docketItemId: string,
    itemStatus: DocketItemStatus,
  ) => Promise<void>;
  orderTypes: OrderType[];
  salesChannels?: SalesChannel[];
  onToggleDocketDetails: (a: DocketDetailsProps) => void;
  enablePrint?: boolean;
  printKitchenDocket?: (
    docketId: string,
    status: DocketStatus,
    notifiers?: NotifierDocketItems[],
  ) => void;
}

const DocketItem: React.FC<DocketItemProps> = ({
  item,
  status,
  onPressDocketHeader,
  onPressDocketItem,
  orderTypes,
  salesChannels = [],
  onToggleDocketDetails,
  enablePrint,
  printKitchenDocket,
}) => {
  const docketItems = item as DocketDisplayOtherItem;

  if (!item) return null;

  switch (item.type) {
    case DocketDisplayItemType.DOCKET_TITLE_BAR:
      return <DocketTitleBar tags={item.tags} rushedAt={item.rushedAt} />;
    case DocketDisplayItemType.TRAINING_DOCKET:
      return <TrainingDocketHeader headerText={item.name} />;
    case DocketDisplayItemType.DOCKET_HEADER:
      const docket = item as unknown as DocketDisplayOrder;
      const docketName = getDockerHeaderTitle({
        orderTypes: orderTypes,
        orderTypeId: docket.orderType,
        tableName: docket.table,
        sectionName: docket.section,
        customerName: docket.customer,
        source: docket.source,
        orderNumber: docket.orderNumber,
      });
      //TODO to get ordertype name in kds event
      const orderTypeName = getOrderTypeName(docket.orderType, orderTypes);
      const salesChannelName = getPartnerSalesChannelName(
        docket.salesChannel,
        salesChannels,
      );
      const docketHeaderColor = getHeaderColorByOrderType(
        docket.orderType,
        orderTypes,
      );
      const handleOnPressDocketHeader = async () => {
        if (onPressDocketHeader) {
          await onPressDocketHeader(docket.id, docket.notifierItems);
        }
      };

      const onSelectDocket = async () => {
        if (bulkSelectVar()) {
          const currentSelection = selectedDocketVar() as Record<
            string,
            DocketDisplayOrder
          >;

          if (currentSelection[docket.id]) {
            const { [docket.id]: _, ...rest } = currentSelection;
            selectedDocketVar(rest);
          } else {
            selectedDocketVar({ ...currentSelection, [docket.id]: docket });
          }
        } else {
          selectedDocketVar({ [docket.id]: docket });
        }
      };
      return (
        <DocketItemHeader
          docketId={docket.id}
          onPressDocketHeader={handleOnPressDocketHeader}
          orderTypeName={orderTypeName}
          salesChannelName={salesChannelName}
          orderNumber={docket.orderNumber}
          docketName={docketName}
          createdTime={docket.time}
          status={status}
          docketHeaderColor={docketHeaderColor}
          docketStatus={docket.status}
          isTraining={docket.isTraining}
          notifierItems={docket.notifierItems}
          onSelectDocket={onSelectDocket}
          enablePrint={enablePrint}
          printKitchenDocket={printKitchenDocket}
        />
      );
    case DocketDisplayItemType.DUE_AT:
      return <DocketDueTime name={item.name} status={status} />;
    case DocketDisplayItemType.ITEM:
      const docketItem = item as DocketDisplayOrderItem;
      const handleOnPressDocketItem = async () => {
        if (onPressDocketItem) {
          if (docketItem?.consolidatedOrderItemIds) {
            await Promise.all(
              docketItem.consolidatedOrderItemIds.map(id =>
                onPressDocketItem(id, docketItem.itemStatus),
              ),
            );
          } else {
            await onPressDocketItem(
              docketItem?.orderItemId,
              docketItem.itemStatus,
            );
          }
        }
      };
      return (
        <DocketDisplayItemComponent
          id={docketItem.id}
          quantity={docketItem.quantity}
          name={docketItem.name}
          note={docketItem.note}
          modifiers={docketItem.modifiers}
          variant={docketItem.variant}
          itemStatus={docketItem.itemStatus}
          status={status}
          onPressDocketItem={handleOnPressDocketItem}
          seatName={docketItem?.seatName}
        />
      );
    case DocketDisplayItemType.COURSE:
    case DocketDisplayItemType.SEAT_NUMBER:
    case DocketDisplayItemType.CATEGORY:
      return (
        <DocketItemBase
          name={item.name}
          primaryGrouping={item.primaryGrouping}
          displayPreference={item.displayPreference}
        />
      );
    case DocketDisplayItemType.ORDER_NOTE:
      return <DocketItemNotes name={item.name} />;
    case DocketDisplayItemType.VOID:
      return <DocketVoidLabel />;
    case DocketDisplayItemType.TRANSFERRED_TO_LABEL:
      return <DocketTransferredLabel tableId={item.name} />;
    case DocketDisplayItemType.TRANSFERRED_FROM_LABEL:
      return (
        <DocketTransferredLabel
          tableId={item.name}
          transferLabelType={DocketDisplayItemType.TRANSFERRED_FROM_LABEL}
        />
      );
    case DocketDisplayItemType.OTHER_ITEMS:
      return (
        <DocketOtherItemsLabel
          docketItem={docketItems}
          onToggleDocketDetails={onToggleDocketDetails}
        />
      );
    case DocketDisplayItemType.RECALLED_TAG:
      return <DocketRecalledLabel />;
    case DocketDisplayItemType.CONTINUED:
      const docketContinued = item as DocketDisplayContinuedItem;
      return (
        <DocketItemContinue
          docketId={docketContinued.docketId}
          text={item.name}
          subtext={item?.position?.pageIndicator || ''}
          orderNumber={item?.orderNumber}
          status={docketContinued.status}
        />
      );
    case DocketDisplayItemType.CONTINUE:
      const docketContinue = item as DocketDisplayContinueItem;
      return (
        <DocketItemContinue
          docketId={docketContinue.docketId}
          text={item.name}
          subtext={item?.position?.pageIndicator || ''}
          status={docketContinue.status}
        />
      );
    case DocketDisplayItemType.RECALL_BUTTON:
      const handleOnPressDocketRecall = () => {
        const docket = item as unknown as DocketDisplayOrder;
        if (onPressDocketHeader) {
          onPressDocketHeader(docket.id, docket.notifierItems);
        }
      };
      return <DocketItemReCall onPressRecall={handleOnPressDocketRecall} />;
    default:
      return null;
  }
};

export default DocketItem;
